import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import fetchService from "Services/getApidataStrapi";
import {
  STRAPI_API_PRIVACY_POLICY_UK,
  STRAPI_API_DISCLOSURE_STATEMENT_RECUR_EN, // fallback (for US, if needed)
} from "Routes/Main/constants";
import { Grid, Paper } from "@material-ui/core";
import CommonLoader from "Views/Common/CommonLoader";
import LayoutView from "Views/Common/LayoutView";
import { SectionHeader } from "components";
import clsx from "clsx";
import { useCountryConfig } from "hooks/useCountryConfig";
import { useInitializeCountryConfig } from "hooks/useInitializeCountryConfig";
import { TERMS_DETAILS_REPLACE_MACROS } from 'Views/Common/constants';
import { GROUP_SERVICES } from 'Services';

const PrivacyPolicy = () => {
  // Initialize and get country config
  useInitializeCountryConfig();
  const { countryCode } = useCountryConfig();
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState("");
  const [title, setTitle] = useState("Privacy Policy");
  const [disclosureData, setDisclosureData] = useState();
  const [detailsLoading, setDetailsLoading] = useState(false);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const groupId = queryParams.get("group_id");
  const planName = queryParams.get("plan_name");


  // Only rely on countryCode from context
  const currentCountryCode = countryCode;

  // Stay false until currentCountryCode is available
  const isReadyToLoad = Boolean(currentCountryCode);

  // Helper to fetch Strapi data (supports old attributes and new document response formats)
  const getStrapiData = async (apiPath) => {
    try {
      const response = await fetchService(apiPath);
      return response;
    }
    catch (err) {
      console.error('Strapi API Error:', err);
      throw err;
    }
  };

  const updateDisclosureContent = (disclosureDetails) => {
    const {
      group_name = '',
      group_url = '',
      email: group_email,
      max_additional_members: group_members = 0,
      patient_website = '',
      industry = '',
      patient_support_email = '',
      phone = ''
    } = disclosureDetails;



    if (disclosureData) {
      // Create the proper structure for the modal with replaced content
      const processedContent = disclosureData
        .replaceAll(TERMS_DETAILS_REPLACE_MACROS.group_name, group_name)
        .replaceAll(TERMS_DETAILS_REPLACE_MACROS.group_url, group_url)
        .replaceAll(TERMS_DETAILS_REPLACE_MACROS.group_email, group_email)
        .replaceAll(TERMS_DETAILS_REPLACE_MACROS.group_members, group_members)
        .replaceAll(TERMS_DETAILS_REPLACE_MACROS.group_patient_portal, patient_website)
        .replaceAll(TERMS_DETAILS_REPLACE_MACROS.group_industry, industry.toLowerCase())
        .replaceAll(TERMS_DETAILS_REPLACE_MACROS.subscribili_support_email, patient_support_email)
        .replaceAll(TERMS_DETAILS_REPLACE_MACROS.phone, phone)
        .replaceAll('{{PLAN_NAME}}', planName || '')
        .replaceAll('{{PROVIDER_NAME}}', group_name || '')
      setContent(processedContent);
    }
  };

  const getDisclosureDetails = async () => {
    if (!groupId || groupId === 'ALL_GROUPS') {
      setDetailsLoading(false);
      return;
    }

    setDetailsLoading(true);
    try {
      const response = await GROUP_SERVICES.getDisclosureDetails(groupId);
      if (response?.type === 'success' && response?.data) {
        updateDisclosureContent(response.data);
      } else {
        throw response;
      }
    } catch (err) {
      console.error("[PrivacyPolicy] Error fetching disclosure details:", err);
    } finally {
      setDetailsLoading(false);
    }
  };

  // Fetch privacy policy content based on country
  const fetchPrivacyContent = async () => {
    setLoading(true);
    try {
      const apiPath =
        currentCountryCode === "GB"
          ? STRAPI_API_PRIVACY_POLICY_UK
          : STRAPI_API_DISCLOSURE_STATEMENT_RECUR_EN; // fallback for US, if needed


      const strapiResponse = await getStrapiData(apiPath);
      


      setTitle(strapiResponse?.data?.title || "Privacy Policy");
      setDisclosureData(strapiResponse?.data?.description);
      
    } catch (err) {
      console.error("[PrivacyPolicy] Error loading policy:", err);
      setContent("Unable to load Privacy Policy. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch Strapi content when country is available
  useEffect(() => {
    if (isReadyToLoad) {
      fetchPrivacyContent();
    }
  }, [currentCountryCode, isReadyToLoad]);

  // Fetch group details when both disclosureData and groupId are available
  useEffect(() => {
    if (disclosureData && groupId) {
      getDisclosureDetails();
    }
  }, [disclosureData, groupId]);

  return (
    <>
      <SectionHeader  
        title=""
        rightSection={
          <Paper className={clsx("widget-wrapper")} elevation={0}>
            {loading || !isReadyToLoad || (groupId && detailsLoading) ? (
              <CommonLoader />
            ) : (
              <Grid item xs={12}>
                {content ? (
                  <div className="content" style={{ fontSize: "16px", color: "#333", lineHeight: "1.6" }}>
                    <style>
                      {`
                        .content p {
                          margin: 0 0 16px 0 !important;
                          line-height: 1.6;
                          font-size: 16px;
                          color: #333;
                        }
                        .content h1, .content h2, .content h3,
                        .content h4, .content h5, .content h6 {
                          margin: 24px 0 16px 0 !important;
                          font-weight: 600;
                          color: #333;
                        }
                        .content h1 { font-size: 28px; }
                        .content h2 { font-size: 26px; }
                        .content h3 { font-size: 22px; }
                        .content h4 { font-size: 20px; }
                        .content h5 { font-size: 18px; }
                        .content h6 { font-size: 14px; }
                        .content ul {
                          margin: 0 0 16px 0 !important;
                          padding-left: 20px;
                        }
                        .content li {
                          margin: 0 0 8px 0 !important;
                          list-style: disc;
                          line-height: 1.5;
                          font-size: 16px;
                          color: #333;
                        }
                        .onlyBackend {
                          text-align: left !important;
                        }
                      `}
                    </style>

                    <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                      {content}
                    </ReactMarkdown>
                  </div>
                ) : (
                  <div>No Privacy Policy found for this region.</div>
                )}
              </Grid>
            )}
          </Paper>
          
        }
      />
    </>
  );
};

export default PrivacyPolicy;
